import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './HomePage.scss';
import { useNavigate } from 'react-router-dom';
import Ticker from './Ticker';

const HomePage = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeTab, setActiveTab] = useState('estateagent');
  const [slug, setSlug] = useState('ai-assistant');
  const widgetLoaded = useRef(false);
  const widgetScriptRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [businessData, setBusinessData] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);

  // Unified widget loader - only one instance
  const loadWidget = (clientId, isDemo = false) => {
    // If widget is already loaded, just trigger it
    if (widgetLoaded.current) {
      triggerWidget();
      if (isDemo) setIsDemoMode(true);
      return;
    }

    // Clean up any existing widget first
    cleanupWidget();

    // Create and load the script
    const script = document.createElement('script');
    script.src = `${process.env.REACT_APP_WIDGET_URL}/widget.js?clientId=${clientId}&apiUrl=${process.env.REACT_APP_API_URL}`;
    script.async = true;
    script.setAttribute('data-client-id', clientId);
    script.id = 'gemini-widget-script';

    script.onload = () => {
      widgetLoaded.current = true;
      widgetScriptRef.current = script;
      if (isDemo) setIsDemoMode(true);

      // Trigger the widget after it loads
      setTimeout(() => {
        triggerWidget();
      }, 500);
    };

    script.onerror = () => {
      console.error('Failed to load widget script');
      widgetLoaded.current = false;
    };

    document.body.appendChild(script);
  };

  // Trigger existing widget
  const triggerWidget = () => {
    const widgetToggle = document.querySelector('.gemini-chat-toggle');
    if (widgetToggle) {
      widgetToggle.click();
    } else {
      // If widget element doesn't exist yet, wait and retry
      setTimeout(() => {
        const retryToggle = document.querySelector('.gemini-chat-toggle');
        if (retryToggle) {
          retryToggle.click();
        } else {
          console.warn('Widget toggle element not found');
        }
      }, 1000);
    }
  };

  // Cleanup widget
  const cleanupWidget = () => {
    // Remove script
    if (widgetScriptRef.current && widgetScriptRef.current.parentNode) {
      widgetScriptRef.current.parentNode.removeChild(widgetScriptRef.current);
    }

    // Remove widget elements
    const widgetElements = document.querySelectorAll(
      '.gemini-chat-toggle, .gemini-widget-container, .gemini-transcript-box'
    );
    widgetElements.forEach(el => el.remove());

    widgetLoaded.current = false;
    widgetScriptRef.current = null;
  };

  // Handle demo request
  const handleDemoRequest = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // If widget is already loaded in demo mode, just trigger it
    if (widgetLoaded.current && isDemoMode) {
      triggerWidget();
      return;
    }

    // Load demo widget
    loadWidget('demo', true);
  };

  // Handle regular widget load (for actual business)
  const handleRegularWidgetLoad = (clientId) => {
    if (!clientId) return;

    // If widget is already loaded but in demo mode, switch to business mode
    if (widgetLoaded.current && isDemoMode) {
      cleanupWidget();
    }

    loadWidget(clientId, false);
  };

  // Function to fetch business data
  const fetchBusinessData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/business/${slug}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      const result = await response.json();

      if (!result.data.business_type) {
        setError('Business not found or failed to load');
        setTimeout(() => {
          navigate('/');
        }, 2000);
        return;
      }

      setBusinessData(result.data);
      setError(null);

      // Load widget only if not in demo mode
      if (result.data.id && !isDemoMode) {
        handleRegularWidgetLoad(result.data.id);
      }

    } catch (err) {
      console.error('Error fetching business data:', err);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (slug) {
      fetchBusinessData();
    }

    return () => {
      cleanupWidget();
    };
  }, [slug]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const features = {
    estateagent: [
      {
        icon: 'fa-headset',
        title: '24/7 AI Receptionist',
        description: 'Your virtual front desk. Handles inbound calls and web chats, answers FAQs, qualifies leads, and books viewings instantly, anytime.'
      },
      {
        icon: 'fa-eye',
        title: 'Smart Booking & Availability',
        description: 'Check, modify, or cancel existing bookings in real-time. See agent availability and scheduled viewings instantly.'
      },
      {
        icon: 'fa-home',
        title: 'Property Availability Search',
        description: 'Instantly check all available properties, filter by criteria, and get AI-recommended matches for client inquiries.'
      },
      {
        icon: 'fa-flag',
        title: 'Issue & Complaint Reporting',
        description: 'Quickly report property issues or client complaints. AI logs, prioritizes, and routes them to the correct team for resolution.'
      },
      {
        icon: 'fa-user-clock',
        title: 'Agent Calendar Management',
        description: 'Centralized booking calendars for all employees. AI optimizes scheduling to prevent conflicts and maximize productivity.'
      },
      {
        icon: 'fa-mobile-alt',
        title: 'Omnichannel Booking',
        description: 'Seamless automated booking system for clients via website and phone, syncing all appointments in real-time.'
      },
      {
        icon: 'fa-comment-dots',
        title: 'Automated SMS Notifications',
        description: 'Automated booking confirmations, reminders, and updates via SMS to both property owners and clients. Ensures everyone stays informed.'
      },
      {
        icon: 'fa-star',
        title: 'Customer Experience & Reputation',
        description: 'AI enhances client satisfaction with instant responses and flawless scheduling, building a professional, modern brand image that attracts more business.'
      }
    ],
    restaurants: [
      {
        icon: 'fa-robot',
        title: 'Smart Order Taking',
        description: 'AI-powered order processing with personalized recommendations'
      },
      {
        icon: 'fa-utensils',
        title: 'Kitchen Automation',
        description: 'Optimized cooking schedules and inventory management'
      },
      {
        icon: 'fa-comments',
        title: 'Customer Service',
        description: '24/7 AI concierge for reservations and inquiries'
      },
      {
        icon: 'fa-mobile-alt',
        title: 'Multi-Channel Ordering',
        description: 'Customers order via web widget, phone, or in-restaurant. AI processes orders and sends instant SMS confirmations.'
      },
      {
        icon: 'fa-bell',
        title: 'Order Status Notifications',
        description: 'Real-time SMS updates at every stage: order received, cooking, ready for pickup, or out for delivery.'
      },
      {
        icon: 'fa-utensils',
        title: 'Smart Order Management',
        description: 'AI coordinates kitchen workflow, prioritizes orders, and manages preparation times for both pickup and delivery.'
      },
      {
        icon: 'fa-motorcycle',
        title: 'Delivery Coordination',
        description: 'Optimizes delivery routes, assigns drivers, and provides customers with live delivery tracking via SMS.'
      },
      {
        icon: 'fa-box',
        title: 'Pickup Optimization',
        description: 'Manages pickup schedules, provides ready-time estimates, and sends "order ready" alerts to prevent waiting.'
      },
      {
        icon: 'fa-comment-dots',
        title: 'Complete SMS Journey',
        description: 'End-to-end SMS communication: order confirmation, cooking status, pickup/delivery alerts, and feedback requests.'
      },
      {
        icon: 'fa-search',
        title: 'Menu Intelligence',
        description: 'AI helps customers discover dishes, filter by preferences, and get personalized recommendations when ordering.'
      },
      {
        icon: 'fa-clock',
        title: 'Order Timing Predictions',
        description: 'Accurate wait time forecasts based on kitchen load, ingredient availability, and current order volume.'
      },
      {
        icon: 'fa-chart-line',
        title: 'Order Analytics',
        description: 'Real-time insights on popular items, peak ordering times, customer preferences, and order completion rates.'
      }
    ],
    garage: [
      {
        icon: 'fa-brain',
        title: 'AI Master Mechanic & Receptionist',
        description: 'More automotive knowledge than all your mechanics combined. Diagnoses with 97% accuracy while handling all customer interactions.'
      },
      {
        icon: 'fa-calendar-alt',
        title: 'Smart Service Booking System',
        description: 'AI books appointments, optimizes workshop schedules, and sends instant SMS confirmations to customers.'
      },
      {
        icon: 'fa-mobile-alt',
        title: 'Customer Booking Portal',
        description: 'Customers check, modify, or cancel bookings 24/7 via web, app or phone. View real-time service progress and technician updates.'
      },
      {
        icon: 'fa-comment-dots',
        title: 'Automated SMS Notifications',
        description: 'Booking confirmations, 24-hour reminders, service progress updates, and pickup ready alerts - all via SMS.'
      },
      {
        icon: 'fa-file-invoice-dollar',
        title: 'Instant Quotes & Upselling',
        description: 'AI generates accurate quotes in seconds and suggests preventative services as valuable add-ons during booking.'
      },
      {
        icon: 'fa-cart-plus',
        title: 'Booking-to-Sale Conversion',
        description: 'Seamlessly converts inquiries into booked services with maintenance packages, increasing average transaction value.'
      },
      {
        icon: 'fa-trophy',
        title: 'Unbeatable Competitive Edge',
        description: 'Offer AI-powered precision and convenience no other garage can match. Become the most trusted garage in town.'
      }
    ],
    selfemployed: [
      {
        icon: 'fa-brain',
        title: 'AI Business Manager',
        description: 'Your virtual partner that handles clients, scheduling, and admin 24/7. You focus on skilled work, AI handles everything else.'
      },
      {
        icon: 'fa-calendar-check',
        title: 'Intelligent Job Booking',
        description: 'AI books jobs around your schedule, optimizes travel routes, and sends professional SMS confirmations to every client.'
      },
      {
        icon: 'fa-laptop-house',
        title: 'Professional Client Portal',
        description: 'Clients book, manage appointments, and track job progress online. Reduces phone calls and makes you look more professional.'
      },
      {
        icon: 'fa-sms',
        title: 'Complete SMS Automation',
        description: '1. Booking confirmed 2. Arrival reminder 3. Job started 4. Completion update 5. Invoice sent - All automated via SMS.'
      },
      {
        icon: 'fa-file-contract',
        title: 'Instant Quote Generator',
        description: 'AI creates professional, itemized quotes in 30 seconds. Clients accept electronically, turning inquiries into booked work.'
      },
      {
        icon: 'fa-medal',
        title: 'Premium Service Packages',
        description: 'AI sells maintenance plans, safety inspections, and premium service packages during booking - increasing your average job value.'
      },
      {
        icon: 'fa-rocket',
        title: 'Scale Your Business',
        description: 'What used to take hours now happens automatically. Handle 3x more clients without working more hours. Grow sustainably.'
      }
    ],
    healthcare: [
      {
        icon: 'fa-stethoscope',
        title: 'AI Medical Receptionist & Triage',
        description: 'Your 24/7 virtual receptionist. Handles appointments, answers FAQs, and performs initial symptom assessment while maintaining patient privacy.'
      },
      {
        icon: 'fa-calendar-check',
        title: 'Smart Appointment Scheduling',
        description: 'AI manages multiple provider calendars (doctors, dentists, specialists), books appointments, and optimizes clinic schedules for maximum efficiency.'
      },
      {
        icon: 'fa-laptop-medical',
        title: 'Patient Self-Service Portal',
        description: 'Patients book, reschedule, or check appointment status 24/7 via web or app. Complete forms online and receive real-time updates.'
      },
      {
        icon: 'fa-bell',
        title: 'Automated Patient Communications',
        description: 'Appointment confirmations, 48/24-hour reminders, wait time updates, and follow-up care instructions - all via SMS or email.'
      },
      {
        icon: 'fa-clipboard-check',
        title: 'Intelligent Treatment Planning',
        description: 'AI suggests follow-up appointments, preventative care, and treatment packages based on patient history and best practices.'
      },
      {
        icon: 'fa-prescription-bottle',
        title: 'Preventative Care & Package Sales',
        description: 'AI recommends dental cleanings, annual check-ups, wellness packages, and preventative treatments during the booking process.'
      },
      {
        icon: 'fa-user-md',
        title: 'Practice Growth & Patient Retention',
        description: 'Reduce no-shows by 80%, fill last-minute cancellations automatically, and build a loyal patient base with superior convenience.'
      }
    ]
  };

  const stats = [
    { value: '3', label: 'Resturants', icon: 'fa-building' },
    { value: '7', label: 'Estate Agents Automated', icon: 'fa-utensils' },
    { value: '98%', label: 'Customer Satisfaction', icon: 'fa-smile' },
    { value: '24/7', label: 'Uptime', icon: 'fa-shield-alt' }
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'Operations Director',
      company: 'Green Properties',
      quote: 'Our phone answering time dropped to 0 seconds with AI handling calls 24/7.',
      avatar: 'SC'
    },
    {
      name: 'Alex cooper',
      role: 'Restaurant Owner',
      company: 'Bella Napoli',
      quote: 'AI handling reservations freed up our staff to focus on hospitality.',
      avatar: 'MR'
    },
    {
      name: 'Ryan Durgacharan',
      role: 'Operations Manager',
      company: 'City Garage Services',
      quote: 'From 20 missed calls daily to zero. Our AI handles booking inquiries and order status updates around the clock.',
      avatar: 'MR'
    }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="row align-items-center min-vh-80 mt-5 mb-5">
            <div className="col-lg-8">
              <div className="hero-content">
                <div className="badge mb-3">
                  <span className="pulse-dot"></span>
                  AI-Powered Platform
                </div>
                <h1 className="display-4 fw-bold mb-4">
                  Intelligent Assistance for
                  <span className="text-gradient"> Estate Agents, Garage, Health Care & Restaurants</span>
                </h1>
                <p className="lead mb-5">
                  Transform your business operations with our AI-driven platform. Double your reach, automate everything. Our AI manages identical operations seamlessly across web and phone channels—bookings, orders, service, and more. Work smarter, not harder, 24/7.
                </p>
                <div className="hero-cta">
                  <button to="/demo" className="btn-glass-primary p-3 mt-3"
                    onClick={handleDemoRequest}
                    style={{ borderRadius: '25px', textTransform: "uppercase" }}
                    

                  >
                    <i className="fas fa-brain fs-3 me-2"></i>
                    Request AI Demo
                  </button>
                  <a
                    href={`tel:+447380328050`}
                    className="btn-glass-primary p-3 mt-3"
                    style={{
                      textDecoration: "none", borderRadius: '25px', 
                      textTransform: "uppercase"
                    }}
                    onMouseEnter={(e) => e.target.style.transform = 'translateY(-3px)'}
                    onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                  >
                    <i className="fas fa-phone-square-alt fs-3 me-2"></i>
                    <span className='ms-2'>Call <span style={{ letterSpacing: "2px" }}>{`+447380328050`}</span></span>
                  </a>


                </div>

              </div>

            </div>
            <div className="col-lg-4">
              <div className="hero-visual">
                <div className="visual-card utility">
                  <i className="fas fa-bolt"></i>
                  <h5>Agent AI</h5>
                  <p>Business Help</p>
                </div>
                <div className="visual-card restaurant">
                  <i className="fas fa-utensils"></i>
                  <h5>Receptionist AI</h5>
                  <p>Take Aways</p>
                </div>
                <div className="ai-core">
                  <i className="fas fa-brain"></i>
                  <span>AI Core</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Ticker />

      {/* Features Tabs */}
      <section className="features-section py-5 mt-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title-h2 mb-5">Industry-Specific AI Solutions</h2>
            <p className="section-subtitle mb-5">
              Tailored AI assistants designed for your specific industry needs
            </p>
          </div>

          <div className="tab-navigation mb-4">
            <button
              className={`tab-button ${activeTab === 'estateagent' ? 'active' : ''}`}
              onClick={() => setActiveTab('estateagent')}
            >
              <i className="fas fa-house-chimney me-2"></i> {/* Modern home icon */}
              Estate Agents & Real Estate
            </button>
            <button
              className={`tab-button ${activeTab === 'restaurants' ? 'active' : ''}`}
              onClick={() => setActiveTab('restaurants')}
            >
              <i className="fas fa-pizza-slice me-2"></i> {/* Fun food icon */}
              Restaurants & Takeaways
            </button>
            <button
              className={`tab-button ${activeTab === 'garage' ? 'active' : ''}`}
              onClick={() => setActiveTab('garage')}
            >
              <i className="fas fa-car-battery me-2"></i> {/* Specific automotive */}
              Car Garages & Mechanics
            </button>
            <button
              className={`tab-button ${activeTab === 'healthcare' ? 'active' : ''}`}
              onClick={() => setActiveTab('healthcare')}
            >
              <i className="fas fa-toolbox me-2"></i> {/* Perfect for tradespeople */}
              Healthcare & Dentists
            </button>
            <button
              className={`tab-button ${activeTab === 'selfemployed' ? 'active' : ''}`}
              onClick={() => setActiveTab('selfemployed')}
            >
              <i className="fas fa-toolbox me-2"></i> {/* Perfect for tradespeople */}
              Self Employed
            </button>
          </div>

          <div className="tab-content">
            <div className="row g-4">
              {features[activeTab].map((feature, index) => (
                <div key={index} className="col-md-6 col-lg-3">
                  <div className="feature-card">
                    <div className="feature-icon">
                      <i className={`fas ${feature.icon}`}></i>
                    </div>
                    <h5>{feature.title}</h5>
                    <p>{feature.description}</p>
                    <Link to={`/features/${activeTab}/${index}`} className="feature-link">
                      Learn more <i className="fas fa-arrow-right"></i>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section py-5 bg-light">
        <div className="container">
          <div className="row g-4">
            {stats.map((stat, index) => (
              <div key={index} className="col-md-3 col-sm-6">
                <div className="stat-card text-center">
                  <div className="stat-icon">
                    <i className={`fas ${stat.icon}`}></i>
                  </div>
                  <h3 className="stat-value">{stat.value}</h3>
                  <p className="stat-label">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live AI Demo */}
      <section className="demo-section py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="demo-content">
                <h2>Experience AI in Action</h2>
                <p className="lead">
                  See how our AI assistant handles real-world scenarios for both Estate Agents, Health Care, Garage, Take Away and restaurants.
                </p>
                <div className="demo-examples">
                  <div className="example-item">
                    <div className="example-icon">
                      <i className="fas fa-plug"></i>
                    </div>
                    <div>
                      <h5>Can I Book for an MOT & OIL service?</h5>
                      <p>"Find me available appointment and notify me via sms"</p>
                    </div>
                  </div>
                  <div className="example-item">
                    <div className="example-icon">
                      <i className="fas fa-plug"></i>
                    </div>
                    <div>
                      <h5>Property Availability Inquiry</h5>
                      <p>"Show me properties near me"</p>
                    </div>
                  </div>
                  <div className="example-item">
                    <div className="example-icon">
                      <i className="fas fa-hamburger"></i>
                    </div>
                    <div>
                      <h5>Restaurant Reservation</h5>
                      <p>"Book a table for 4 at 7 PM tomorrow"</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="demo-interface">
                <div className="chat-header">
                  <div className="chat-avatar">
                    <i className="fas fa-robot"></i>
                  </div>
                  <div>
                    <h5>AI Assistant</h5>
                    <small>Online • Ready to help</small>
                  </div>
                  <div className="current-time">
                    {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                <div className="chat-body">
                  <div className="message ai">
                    <div className="message-content">
                      Hello! I'm your AI assistant. How can I help you today?
                    </div>
                    <div className="message-time">Now</div>
                  </div>
                  <div className="message user">
                    <div className="message-content">
                      Can I make a booking. What do you have available ?
                    </div>
                    <div className="message-time">Now</div>
                  </div>
                  <div className="message ai typing">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
                <div className="chat-input">
                  <input type="text" placeholder="Type your message..." />
                  <button className="send-btn">
                    <i className="fas fa-paper-plane"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title">Trusted by Industry Leaders</h2>
          </div>
          <div className="row g-4">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="col-md-4">
                <div className="testimonial-card">
                  <div className="testimonial-content">
                    <i className="fas fa-quote-left"></i>
                    <p>{testimonial.quote}</p>
                  </div>
                  <div className="testimonial-author">
                    <div className="author-avatar">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <h6>{testimonial.name}</h6>
                      <p>{testimonial.role} • {testimonial.company}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5">
        <div className="container">
          <div className="cta-card">
            <div className="row align-items-center">
              <div className="col-lg-8">
                <h2>Ready to Transform Your Business?</h2>
                <p>Join thousands of utilities and restaurants already benefiting from AI automation.</p>
              </div>
              <div className="col-lg-4 text-lg-end">
                <Link to="/signup" className="btn btn-primary btn-lg me-3">
                  Start Free Trial
                </Link>
                <Link to="/contact" className="btn btn-outline-light btn-lg">
                  Contact Sales
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-4 mb-4">
              <div className="footer-brand">
                <i className="fas fa-brain me-2"></i>
                AI Assistant Pro
              </div>
              <p className="footer-description">
                Empowering utilities and restaurants with intelligent automation and AI-driven solutions.
              </p>
            </div>
            <div className="col-lg-2 col-md-4 mb-4">
              <h5>Solutions</h5>
              <ul className="footer-links">
                <li><Link to="/utilities">Utilities</Link></li>
                <li><Link to="/restaurants">Restaurants</Link></li>
                <li><Link to="/energy">Energy Management</Link></li>
                <li><Link to="/hospitality">Hospitality</Link></li>
              </ul>
            </div>
            <div className="col-lg-2 col-md-4 mb-4">
              <h5>Resources</h5>
              <ul className="footer-links">
                <li><Link to="/docs">Documentation</Link></li>
                <li><Link to="/api">API Reference</Link></li>
                <li><Link to="/blog">Blog</Link></li>
                <li><Link to="/support">Support</Link></li>
              </ul>
            </div>
            <div className="col-lg-4 col-md-4 mb-4">
              <h5>Stay Updated</h5>
              <p>Subscribe to our newsletter for AI insights and updates.</p>
              <div className="newsletter-form">
                <input type="email" placeholder="Your email address" />
                <button type="submit">
                  <i className="fas fa-paper-plane"></i>
                </button>
              </div>
            </div>
          </div>

        </div>
      </footer>
    </div>
  );
};

export default HomePage;